import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger } from 'nuqs';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { PermissionButton } from '@/components/ui/permission-button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Eye, FormInput, Edit, Trash2, FolderPlus, Folder, MoveHorizontal, ChevronLeft, ChevronUp, ChevronDown, GripVertical, X, Code, Copy, Check, ExternalLink, FileText } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { FormField as FormFieldConfig, FormFieldType } from '@shared/schema';
import { FormFieldBuilder } from '@/components/FormFieldBuilder';
import { MarketingPageHeader } from '@/components/marketing/MarketingPageHeader';

const leadFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  submitText: z.string().optional(),
  successMessage: z.string().optional(),
  redirectUrl: z.string().optional(),
  workflowId: z.string().optional(),
});

const FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select (Dropdown)' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio' },
  { value: 'date', label: 'Date' },
];

export default function WebToLeadForms() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isPreviewFormOpen, setIsPreviewFormOpen] = useState(false);
  const [isEmbedDialogOpen, setIsEmbedDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [embedCopied, setEmbedCopied] = useState(false);
  
  // NUQS: URL-based state management for shareable URLs
  const [folderId, setFolderId] = useQueryState("folder", parseAsInteger); // Selected folder ID for navigation

  // Folder management state
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<any>(null);
  const [folderForm, setFolderForm] = useState({ name: "", description: "" });
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [selectedFormForMove, setSelectedFormForMove] = useState<any>(null);
  const [formToDelete, setFormToDelete] = useState<any>(null);
  const [isSubmissionsOpen, setIsSubmissionsOpen] = useState(false);
  const [selectedFormForSubmissions, setSelectedFormForSubmissions] = useState<any>(null);

  // Sorting state
  const [formSortField, setFormSortField] = useState<'name' | 'title' | 'url' | 'status' | null>(null);
  const [formSortDirection, setFormSortDirection] = useState<'asc' | 'desc'>('asc');

  // Form field management state
  const [formFields, setFormFields] = useState<FormFieldConfig[]>([
    { id: '1', name: 'name', label: 'Name', type: 'text', required: true },
    { id: '2', name: 'email', label: 'Email', type: 'email', required: true },
    { id: '3', name: 'phone', label: 'Phone', type: 'phone', required: false },
    { id: '4', name: 'message', label: 'Message', type: 'textarea', required: false, placeholder: 'How can we help you?' }
  ]);
  const [submitAction, setSubmitAction] = useState<'message' | 'redirect'>('message');

  // Form for creating/editing lead forms
  const formForm = useForm({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      name: '',
      title: '',
      description: '',
      submitText: 'Submit',
      successMessage: 'Thank you for your submission!',
      redirectUrl: '',
    },
  });

  // Fetch lead forms
  const { data: leadForms = [] } = useQuery<any[]>({
    queryKey: ['/api/workflows/lead-forms'],
    queryFn: async () => {
      const response = await fetch('/api/workflows/lead-forms');
      if (!response.ok) throw new Error('Failed to fetch forms');
      return response.json();
    },
  });

  // Fetch lead form folders
  const { data: folders = [] } = useQuery<any[]>({
    queryKey: ['/api/workflows/lead-form-folders'],
    queryFn: async () => {
      const response = await fetch('/api/workflows/lead-form-folders');
      if (!response.ok) throw new Error('Failed to fetch folders');
      return response.json();
    },
  });

  // Derive currentFolderView from folderId URL param
  const currentFolderView = folderId ? folders.find((f: any) => f.id === folderId) || null : null;

  // Sorting functions
  const handleFormSort = (field: 'name' | 'title' | 'url' | 'status') => {
    if (formSortField === field) {
      setFormSortDirection(formSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setFormSortField(field);
      setFormSortDirection('asc');
    }
  };

  const sortForms = (formsArray: any[]) => {
    if (!formSortField) return formsArray;

    return [...formsArray].sort((a, b) => {
      let aValue: string | boolean;
      let bValue: string | boolean;

      switch (formSortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'url':
          aValue = `/forms/${a.id}`.toLowerCase();
          bValue = `/forms/${b.id}`.toLowerCase();
          break;
        case 'status':
          aValue = a.isActive;
          bValue = b.isActive;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return formSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return formSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableTableHead = ({ 
    field, 
    label, 
    className = "" 
  }: { 
    field: 'name' | 'title' | 'url' | 'status'; 
    label: string;
    className?: string;
  }) => {
    const isSorted = formSortField === field;
    return (
      <TableHead 
        className={`cursor-pointer select-none hover:bg-muted/50 ${className}`}
        onClick={() => handleFormSort(field)}
        data-testid={`sort-${field}`}
      >
        <div className="flex items-center gap-2">
          {label}
          {isSorted ? (
            formSortDirection === 'asc' ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
          ) : (
            <div className="flex flex-col h-4 w-4 opacity-40">
              <ChevronUp className="h-2.5 w-4 -mb-1" />
              <ChevronDown className="h-2.5 w-4" />
            </div>
          )}
        </div>
      </TableHead>
    );
  };

  // Create form mutation
  const createFormMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log('Making API request to:', '/api/workflows/lead-forms');
      console.log('Request data:', JSON.stringify(data, null, 2));
      
      try {
        const response = await fetch('/api/workflows/lead-forms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });
        
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }
        
        const result = await response.json();
        console.log('Success response:', result);
        return result;
      } catch (error) {
        console.error('Fetch error:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Form created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/lead-forms'] });
      setIsCreateFormOpen(false);
      formForm.reset();
      toast({ title: 'Success', description: 'Lead form created successfully' });
    },
    onError: (error) => {
      console.error('Form creation error:', error);
      toast({ 
        title: 'Error', 
        description: `Failed to create lead form: ${error.message}`, 
        variant: 'destructive' 
      });
    },
  });

  // Update form mutation
  const updateFormMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log('Updating form with data:', JSON.stringify(data, null, 2));
      
      const response = await fetch(`/api/workflows/lead-forms/${data.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      return await response.json();
    },
    onSuccess: (data) => {
      console.log('Form updated successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/lead-forms'] });
      setIsEditFormOpen(false);
      setSelectedForm(null);
      formForm.reset();
      toast({ title: 'Success', description: 'Lead form updated successfully' });
    },
    onError: (error) => {
      console.error('Form update error:', error);
      toast({ 
        title: 'Error', 
        description: `Failed to update lead form: ${error.message}`, 
        variant: 'destructive' 
      });
    },
  });

  // Delete form mutation
  const deleteFormMutation = useMutation({
    mutationFn: async (formId: number) => {
      const response = await fetch(`/api/workflows/lead-forms/${formId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/lead-forms'] });
      setFormToDelete(null);
      toast({ title: 'Success', description: 'Form deleted successfully' });
    },
    onError: (error) => {
      toast({ 
        title: 'Error', 
        description: `Failed to delete form: ${error.message}`, 
        variant: 'destructive' 
      });
    },
  });

  // Folder mutations
  const createFolderMutation = useMutation({
    mutationFn: async (folder: { name: string; description?: string }) => {
      const response = await apiRequest('POST', '/api/workflows/lead-form-folders', folder);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/lead-form-folders'] });
      setIsFolderDialogOpen(false);
      setFolderForm({ name: "", description: "" });
      toast({ title: "Folder created successfully" });
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, ...folder }: { id: string; name: string; description?: string }) => {
      const response = await apiRequest('PUT', `/api/workflows/lead-form-folders/${id}`, folder);
      return await response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/lead-form-folders'] });
      if (currentFolderView && data.id === currentFolderView.id) {
        setFolderId(data.id);
      }
      setIsFolderDialogOpen(false);
      setSelectedFolder(null);
      setFolderForm({ name: "", description: "" });
      toast({ title: "Folder updated successfully" });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/workflows/lead-form-folders/${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/lead-form-folders'] });
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/lead-forms'] });
      toast({ title: "Folder deleted successfully" });
    },
  });

  const moveFormMutation = useMutation({
    mutationFn: async ({ formId, folderId }: { formId: string; folderId: string | null }) => {
      const response = await apiRequest('PUT', `/api/workflows/lead-forms/${formId}/move`, { folderId });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/lead-forms'] });
      setIsMoveDialogOpen(false);
      setSelectedFormForMove(null);
      toast({ title: "Form moved successfully" });
    },
  });

  // Folder handlers
  const handleCreateFolder = () => {
    if (!folderForm.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Folder name is required",
        variant: "destructive"
      });
      return;
    }
    createFolderMutation.mutate(folderForm);
  };

  const handleUpdateFolder = () => {
    if (selectedFolder) {
      updateFolderMutation.mutate({
        id: selectedFolder.id,
        ...folderForm,
      });
    }
  };

  const handleEditFolder = (folder: any) => {
    setSelectedFolder(folder);
    setFolderForm({
      name: folder.name,
      description: folder.description || "",
    });
    setIsFolderDialogOpen(true);
  };

  const handleDeleteFolder = (folderId: string) => {
    if (confirm("Delete this folder? Forms in this folder will be moved to no folder.")) {
      deleteFolderMutation.mutate(folderId);
    }
  };

  const handleMoveForm = (form: any) => {
    setSelectedFormForMove(form);
    setIsMoveDialogOpen(true);
  };

  const handleConfirmMoveForm = (folderId: string | null) => {
    if (selectedFormForMove) {
      moveFormMutation.mutate({
        formId: selectedFormForMove.id,
        folderId,
      });
    }
  };

  const onCreateForm = (data: any) => {
    console.log('Form submitted with data:', data);
    console.log('Form errors:', formForm.formState.errors);
    const formData = {
      ...data,
      fields: formFields,
      submitText: data.submitText || 'Submit',
      submitAction: submitAction, // Include submitAction
      successMessage: submitAction === 'message' ? (data.successMessage || 'Thank you for your submission!') : null,
      redirectUrl: submitAction === 'redirect' ? data.redirectUrl : null,
    };
    console.log('Submitting form data:', formData);
    createFormMutation.mutate(formData);
  };

  const onUpdateForm = (data: any) => {
    console.log('Form updated with data:', data);
    const formData = {
      ...data,
      id: selectedForm.id,
      fields: formFields,
      submitText: data.submitText || 'Submit',
      submitAction: submitAction, // Include submitAction
      successMessage: submitAction === 'message' ? (data.successMessage || 'Thank you for your submission!') : null,
      redirectUrl: submitAction === 'redirect' ? data.redirectUrl : null,
    };
    console.log('Updating form data:', formData);
    updateFormMutation.mutate(formData);
  };

  const handleEditForm = (form: any) => {
    setSelectedForm(form);
    formForm.reset({
      name: form.name,
      title: form.title,
      description: form.description || '',
      submitText: form.submitText || 'Submit',
      successMessage: form.successMessage || 'Thank you for your submission!',
      redirectUrl: form.redirectUrl || '',
    });
    
    // Load fields from form
    if (form.fields && Array.isArray(form.fields)) {
      setFormFields(form.fields);
    } else {
      // Default fields if none exist
      setFormFields([
        { id: '1', name: 'name', label: 'Name', type: 'text', required: true },
        { id: '2', name: 'email', label: 'Email', type: 'email', required: true },
        { id: '3', name: 'phone', label: 'Phone', type: 'phone', required: false },
        { id: '4', name: 'message', label: 'Message', type: 'textarea', required: false, placeholder: 'How can we help you?' }
      ]);
    }
    
    // Determine submit action - use explicit submitAction if available, otherwise infer
    if (form.submitAction) {
      setSubmitAction(form.submitAction);
    } else if (form.redirectUrl) {
      setSubmitAction('redirect');
    } else {
      setSubmitAction('message');
    }
    
    setIsEditFormOpen(true);
  };

  const handlePreviewForm = (form: any) => {
    setSelectedForm(form);
    setIsPreviewFormOpen(true);
  };

  const handleEmbedForm = (form: any) => {
    setSelectedForm(form);
    setEmbedCopied(false);
    setIsEmbedDialogOpen(true);
  };

  const handleDeleteForm = (form: any) => {
    setFormToDelete(form);
  };

  const confirmDeleteForm = () => {
    if (formToDelete) {
      deleteFormMutation.mutate(formToDelete.id);
    }
  };

  const handleViewSubmissions = (form: any) => {
    setSelectedFormForSubmissions(form);
    setIsSubmissionsOpen(true);
  };

  // Query submissions for selected form
  const { data: submissions = [] } = useQuery<any[]>({
    queryKey: ['/api/workflows/lead-forms/submissions', selectedFormForSubmissions?.id],
    queryFn: async () => {
      if (!selectedFormForSubmissions?.id) return [];
      const response = await fetch(`/api/workflows/lead-forms/${selectedFormForSubmissions.id}/submissions`);
      if (!response.ok) throw new Error('Failed to fetch submissions');
      return response.json();
    },
    enabled: !!selectedFormForSubmissions?.id && isSubmissionsOpen,
  });

  const handleCopyEmbedCode = async (embedCode: string) => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setEmbedCopied(true);
      toast({ title: 'Copied!', description: 'Embed code copied to clipboard' });
      setTimeout(() => setEmbedCopied(false), 2000);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to copy embed code', variant: 'destructive' });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    console.log('Form submit event triggered');
    e.preventDefault();
    
    try {
      const isValid = await formForm.trigger();
      console.log('Form validation result:', isValid);
      console.log('Form values:', formForm.getValues());
      console.log('Form errors:', formForm.formState.errors);
      
      if (isValid) {
        const data = formForm.getValues();
        
        // Validate submit action configuration
        if (submitAction === 'redirect' && !data.redirectUrl) {
          toast({
            title: 'Validation Error',
            description: 'Redirect URL is required when using redirect submit action',
            variant: 'destructive'
          });
          return;
        }
        
        console.log('Calling form handler with:', data);
        if (selectedForm) {
          onUpdateForm(data);
        } else {
          onCreateForm(data);
        }
      } else {
        console.error('Form validation failed');
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="space-y-6">
      <MarketingPageHeader
        icon={FormInput}
        iconColor="bg-purple-500"
        title="Web-to-Lead Forms"
        description="Create forms that capture leads and trigger automated follow-up sequences"
        actions={
          <div className="flex gap-2">
            <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
              <DialogTrigger asChild>
                <PermissionButton
                  permission="marketing:edit"
                  whenDenied="hide"
                  variant="outline"
                  onClick={() => {
                    setSelectedFolder(null);
                    setFolderForm({ name: "", description: "" });
                  }}
                >
                  <FolderPlus className="mr-2 h-4 w-4" />
                  Add Folder
                </PermissionButton>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {selectedFolder ? 'Edit Folder' : 'Create Folder'}
                </DialogTitle>
                <DialogDescription>
                  {selectedFolder ? 'Update folder details' : 'Create a new folder to organize your forms'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="folderName">Folder Name</Label>
                  <Input
                    id="folderName"
                    value={folderForm.name}
                    onChange={(e) => setFolderForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter folder name"
                  />
                </div>
                <div>
                  <Label htmlFor="folderDescription">Description (Optional)</Label>
                  <Textarea
                    id="folderDescription"
                    value={folderForm.description}
                    onChange={(e) => setFolderForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter folder description"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsFolderDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={selectedFolder ? handleUpdateFolder : handleCreateFolder}
                  disabled={createFolderMutation.isPending || updateFolderMutation.isPending || !folderForm.name.trim()}
                >
                  {createFolderMutation.isPending || updateFolderMutation.isPending ? 'Processing...' : (selectedFolder ? 'Update Folder' : 'Create Folder')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
            <Dialog open={isCreateFormOpen} onOpenChange={setIsCreateFormOpen}>
              <DialogTrigger asChild>
                <PermissionButton
                  permission="marketing:edit"
                  whenDenied="hide"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Form
                </PermissionButton>
              </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Lead Form</DialogTitle>
              <DialogDescription>
                Create a new web-to-lead form with custom fields and submission handling
              </DialogDescription>
            </DialogHeader>
            <Form {...formForm}>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="fields">Form Fields</TabsTrigger>
                    <TabsTrigger value="submit">Submit Action</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4 pt-4">
                    <FormField
                      control={formForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Form Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Contact Form" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formForm.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Form Title *</FormLabel>
                          <FormControl>
                            <Input placeholder="Get in Touch" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Fill out the form below and we'll get back to you..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TabsContent>

                  <TabsContent value="fields" className="space-y-4 pt-4">
                    <FormFieldBuilder
                      fields={formFields}
                      onFieldsChange={setFormFields}
                    />
                  </TabsContent>

                  <TabsContent value="submit" className="space-y-4 pt-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Submit Button Text</Label>
                        <FormField
                          control={formForm.control}
                          name="submitText"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="Submit" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>After Submission</Label>
                        <RadioGroup value={submitAction} onValueChange={(value: 'message' | 'redirect') => setSubmitAction(value)}>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="message" id="message" />
                            <Label htmlFor="message" className="cursor-pointer">Show success message</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="redirect" id="redirect" />
                            <Label htmlFor="redirect" className="cursor-pointer">Redirect to URL</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {submitAction === 'message' && (
                        <FormField
                          control={formForm.control}
                          name="successMessage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Success Message</FormLabel>
                              <FormControl>
                                <Textarea placeholder="Thank you for your submission! We'll be in touch soon." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {submitAction === 'redirect' && (
                        <FormField
                          control={formForm.control}
                          name="redirectUrl"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Redirect URL</FormLabel>
                              <FormControl>
                                <Input placeholder="https://example.com/thank-you" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsCreateFormOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createFormMutation.isPending}>
                    {createFormMutation.isPending ? 'Creating...' : 'Create Form'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
          </div>
        }
      />

      {/* Edit Form Dialog */}
      <div>
        <Dialog open={isEditFormOpen} onOpenChange={setIsEditFormOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Lead Form</DialogTitle>
              <DialogDescription>
                Update your web-to-lead form with custom fields and submission handling
              </DialogDescription>
            </DialogHeader>
            <Form {...formForm}>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="fields">Form Fields</TabsTrigger>
                    <TabsTrigger value="submit">Submit Action</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4 pt-4">
                    <FormField
                      control={formForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Form Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Contact Form" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formForm.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Form Title *</FormLabel>
                          <FormControl>
                            <Input placeholder="Get in Touch" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Fill out the form below and we'll get back to you..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TabsContent>

                  <TabsContent value="fields" className="space-y-4 pt-4">
                    <FormFieldBuilder
                      fields={formFields}
                      onFieldsChange={setFormFields}
                    />
                  </TabsContent>

                  <TabsContent value="submit" className="space-y-4 pt-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Submit Button Text</Label>
                        <FormField
                          control={formForm.control}
                          name="submitText"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="Submit" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>After Submission</Label>
                        <RadioGroup value={submitAction} onValueChange={(value: 'message' | 'redirect') => setSubmitAction(value)}>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="message" id="edit-message" />
                            <Label htmlFor="edit-message" className="cursor-pointer">Show success message</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="redirect" id="edit-redirect" />
                            <Label htmlFor="edit-redirect" className="cursor-pointer">Redirect to URL</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {submitAction === 'message' && (
                        <FormField
                          control={formForm.control}
                          name="successMessage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Success Message</FormLabel>
                              <FormControl>
                                <Textarea placeholder="Thank you for your submission! We'll be in touch soon." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {submitAction === 'redirect' && (
                        <FormField
                          control={formForm.control}
                          name="redirectUrl"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Redirect URL</FormLabel>
                              <FormControl>
                                <Input placeholder="https://example.com/thank-you" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => {
                    setIsEditFormOpen(false);
                    setSelectedForm(null);
                    formForm.reset();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateFormMutation.isPending}>
                    {updateFormMutation.isPending ? 'Updating...' : 'Update Form'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Preview Form Dialog */}
        <Dialog open={isPreviewFormOpen} onOpenChange={setIsPreviewFormOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Form Preview: {selectedForm?.name}</DialogTitle>
              <DialogDescription>
                This is how your form will appear to visitors
              </DialogDescription>
            </DialogHeader>
            {selectedForm && (
              <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold">{selectedForm.title}</h2>
                  {selectedForm.description && (
                    <p className="text-gray-600">{selectedForm.description}</p>
                  )}
                </div>
                <form className="space-y-4">
                  {selectedForm.fields && selectedForm.fields.map((field: any, index: number) => (
                    <div key={index} className="space-y-2">
                      <Label htmlFor={field.id || `field-${index}`}>
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      
                      {/* Textarea */}
                      {field.type === 'textarea' && (
                        <Textarea 
                          id={field.id || `field-${index}`} 
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} 
                        />
                      )}
                      
                      {/* Select Dropdown */}
                      {field.type === 'select' && (
                        <Select>
                          <SelectTrigger id={field.id || `field-${index}`}>
                            <SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options && field.options.map((option: string, optIndex: number) => (
                              <SelectItem key={optIndex} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {/* Radio Buttons */}
                      {field.type === 'radio' && (
                        <div className="space-y-2">
                          {field.options && field.options.map((option: string, optIndex: number) => (
                            <div key={optIndex} className="flex items-center space-x-2">
                              <input 
                                type="radio" 
                                id={`${field.id || `field-${index}`}-${optIndex}`}
                                name={field.id || `field-${index}`}
                                value={option}
                                className="h-4 w-4"
                              />
                              <Label htmlFor={`${field.id || `field-${index}`}-${optIndex}`} className="font-normal cursor-pointer">
                                {option}
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Checkbox */}
                      {field.type === 'checkbox' && (
                        <div className="flex items-center space-x-2">
                          <Checkbox id={field.id || `field-${index}`} />
                          <Label htmlFor={field.id || `field-${index}`} className="font-normal cursor-pointer">
                            {field.placeholder || field.label}
                          </Label>
                        </div>
                      )}
                      
                      {/* Standard Input Fields (text, email, phone, number, date) */}
                      {['text', 'email', 'phone', 'number', 'date'].includes(field.type) && (
                        <Input 
                          id={field.id || `field-${index}`} 
                          type={field.type === 'phone' ? 'tel' : field.type} 
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} 
                        />
                      )}
                    </div>
                  ))}
                  <Button type="button" className="w-full">
                    {selectedForm.submitText || 'Submit'}
                  </Button>
                </form>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Embed Dialog */}
        <Dialog open={isEmbedDialogOpen} onOpenChange={setIsEmbedDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Embed Form: {selectedForm?.name}</DialogTitle>
              <DialogDescription>
                Copy the code below and paste it into your website's HTML
              </DialogDescription>
            </DialogHeader>
            {selectedForm && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Embed Code</Label>
                  <div className="relative">
                    <Textarea
                      value={`<iframe src="${window.location.origin}/embed/forms/${selectedForm.id}" width="100%" height="600" frameborder="0"></iframe>`}
                      readOnly
                      rows={4}
                      className="font-mono text-sm pr-20"
                    />
                    <Button
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => handleCopyEmbedCode(`<iframe src="${window.location.origin}/embed/forms/${selectedForm.id}" width="100%" height="600" frameborder="0"></iframe>`)}
                    >
                      {embedCopied ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This iframe will embed your form on any website. You can customize the width and height attributes as needed.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Direct Link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={`${window.location.origin}/embed/forms/${selectedForm.id}`}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyEmbedCode(`${window.location.origin}/embed/forms/${selectedForm.id}`)}
                    >
                      {embedCopied ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`${window.location.origin}/embed/forms/${selectedForm.id}`, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Open
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Share this link directly or use it in your iframe src attribute.
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">How to use:</h4>
                  <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Copy the embed code above</li>
                    <li>Paste it into your website's HTML where you want the form to appear</li>
                    <li>The form will be fully functional with all your custom fields</li>
                    <li>Submissions will be tracked in your lead management system</li>
                  </ul>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Breadcrumb Navigation */}
      {currentFolderView && (
        <div className="flex items-center space-x-2 mb-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setFolderId(null)}
            className="text-blue-600 hover:bg-blue-50 hover:text-black"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to All Folders
          </Button>
          <span className="text-muted-foreground">/</span>
          <span className="font-medium">{currentFolderView.name}</span>
        </div>
      )}

      {/* Folder View - Show forms inside selected folder */}
      {currentFolderView ? (
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 py-4">
              <div className="flex items-center gap-2">
                <Folder className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-lg font-medium">{currentFolderView.name}</h4>
                    <Badge variant="secondary">
                      {leadForms.filter((f: any) => f.folderId === currentFolderView.id).length} forms
                    </Badge>
                  </div>
                  {currentFolderView.description && (
                    <p className="text-sm text-muted-foreground mt-1">{currentFolderView.description}</p>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditFolder(currentFolderView)}
              >
                <Edit className="h-4 w-4" />
                Edit Folder
              </Button>
            </CardHeader>
          </Card>

          {leadForms.filter((f: any) => f.folderId === currentFolderView.id).length > 0 ? (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead field="name" label="Name" />
                    <SortableTableHead field="title" label="Title" />
                    <SortableTableHead field="url" label="Form URL" />
                    <SortableTableHead field="status" label="Status" />
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortForms(leadForms.filter((f: any) => f.folderId === currentFolderView.id))
                    .map((form: any) => (
                    <TableRow key={form.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FormInput className="h-4 w-4 text-muted-foreground" />
                          {form.name}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md truncate">{form.title}</TableCell>
                      <TableCell>
                        <code className="bg-muted px-2 py-1 rounded text-xs">
                          /forms/{form.id}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={form.isActive ? 'default' : 'secondary'}>
                          {form.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMoveForm(form)}
                            className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                            data-testid={`button-move-${form.id}`}
                          >
                            <MoveHorizontal className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewSubmissions(form)}
                            className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                            data-testid={`button-submissions-${form.id}`}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handlePreviewForm(form)}
                            className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                            data-testid={`button-preview-${form.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEmbedForm(form)}
                            className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                            data-testid={`button-embed-${form.id}`}
                          >
                            <Code className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditForm(form)}
                            className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                            data-testid={`button-edit-${form.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteForm(form)}
                            className="text-red-600 hover:!text-white hover:!bg-red-600"
                            data-testid={`button-delete-${form.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No forms in this folder yet.
            </p>
          )}
        </div>
      ) : (
        /* Main View - Show folders and unorganized forms */
        <div className="space-y-6">
          {/* Folders Section */}
          {folders.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-md font-medium text-muted-foreground">Folders</h4>
              <div className="grid gap-3">
                {folders.map((folder: any) => {
                  const folderFormCount = leadForms.filter((f: any) => f.folderId === folder.id).length;
                  return (
                    <Card key={folder.id} className="cursor-pointer hover:bg-accent/50 transition-colors">
                      <CardHeader className="pb-3">
                        <div
                          className="flex items-center justify-between"
                          onClick={() => setFolderId(folder.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                              <Folder className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-medium">{folder.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {folderFormCount} form{folderFormCount !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditFolder(folder)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteFolder(folder.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Unorganized Forms Section */}
          {(() => {
            const unorganizedForms = leadForms.filter((f: any) => !f.folderId);
            
            return (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h4 className="text-md font-medium text-muted-foreground">
                    Web-to-Lead Forms
                  </h4>
                  <Badge variant="outline">{unorganizedForms.length}</Badge>
                </div>
                {unorganizedForms.length > 0 ? (
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableTableHead field="name" label="Name" />
                          <SortableTableHead field="title" label="Title" />
                          <SortableTableHead field="url" label="Form URL" />
                          <SortableTableHead field="status" label="Status" />
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortForms(unorganizedForms).map((form: any) => (
                        <TableRow key={form.id} className="hover:bg-muted/50">
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <FormInput className="h-4 w-4 text-muted-foreground" />
                              {form.name}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-md truncate">{form.title}</TableCell>
                          <TableCell>
                            <code className="bg-muted px-2 py-1 rounded text-xs">
                              /forms/{form.id}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant={form.isActive ? 'default' : 'secondary'}>
                              {form.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {folders.length > 0 && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMoveForm(form)}
                                  className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                  data-testid={`button-move-${form.id}`}
                                >
                                  <MoveHorizontal className="h-4 w-4" />
                                </Button>
                              )}
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleViewSubmissions(form)}
                                className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-submissions-${form.id}`}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handlePreviewForm(form)}
                                className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-preview-${form.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleEmbedForm(form)}
                                className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-embed-${form.id}`}
                              >
                                <Code className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleEditForm(form)}
                                className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-edit-${form.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDeleteForm(form)}
                                className="text-red-600 hover:!text-white hover:!bg-red-600"
                                data-testid={`button-delete-${form.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    {folders.length > 0 
                      ? 'No unorganized forms. All forms are in folders.' 
                      : 'No forms created yet. Click "New Form" to get started.'}
                  </p>
                )}
              </div>
            );
          })()}

          {leadForms.length === 0 && folders.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              No forms created yet. Click "New Form" to get started.
            </p>
          )}
        </div>
      )}

      {/* Move Form Dialog */}
      <Dialog open={isMoveDialogOpen} onOpenChange={setIsMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Form</DialogTitle>
            <DialogDescription>
              Select a folder to move "{selectedFormForMove?.name}" to
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleConfirmMoveForm(null)}
            >
              <FormInput className="h-4 w-4 mr-2" />
              No Folder (Unorganized)
            </Button>
            {folders.map((folder: any) => (
              <Button
                key={folder.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleConfirmMoveForm(folder.id)}
              >
                <Folder className="h-4 w-4 mr-2" />
                {folder.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Form Confirmation */}
      <AlertDialog open={!!formToDelete} onOpenChange={() => setFormToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{formToDelete?.name}"? This action cannot be undone.
              All submissions for this form will remain in the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteForm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submissions Dialog */}
      <Dialog open={isSubmissionsOpen} onOpenChange={setIsSubmissionsOpen}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Form Submissions: {selectedFormForSubmissions?.name}</DialogTitle>
            <DialogDescription>
              View all submissions for this form
            </DialogDescription>
          </DialogHeader>
          
          {submissions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No submissions yet for this form.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Total submissions: {submissions.length}
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Prospect</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((submission: any) => (
                    <TableRow key={submission.id}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(submission.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-md">
                          {Object.entries(submission.submittedData || {}).map(([key, value]) => (
                            <div key={key} className="text-xs mb-1">
                              <span className="font-medium">{key}:</span>{' '}
                              <span className="text-muted-foreground">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {submission.prospectId ? (
                          <Badge variant="default">Prospect #{submission.prospectId}</Badge>
                        ) : (
                          <Badge variant="secondary">No prospect</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}